import { Container, Typography } from '@mui/material';
import { Formik, Form, type FormikHelpers } from 'formik';
import type { FC } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate, Navigate, useLocation } from 'react-router';
import * as Yup from 'yup';

import Auth from '../../api/auth';
import Button from '../../components/Buttons/Button';
import { FormikTextField } from '../../components/Formik';
import { isValidPassword } from '../../helpers/validation';
import { enqueueSnackbar } from '../../redux/notifier/actions';

interface NewPasswordFormData {
  password1: string;
  password2: string;
}

const NewPassword: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const verificationToken = location.pathname.split('/').pop();

  const ValidationSchema = Yup.object().shape({
    password1: Yup.string()
      .test('Strong password', intl.formatMessage({ id: 'ERR_PASSWORD_INVALID' }), value =>
        value ? isValidPassword(value) : true
      )
      .required(intl.formatMessage({ id: 'formRequiredError' })),
    password2: Yup.string()
      .oneOf([Yup.ref('password1'), undefined], intl.formatMessage({ id: 'userPasswordMismatch' }))
      .required(intl.formatMessage({ id: 'formRequiredError' })),
  });

  const resetUserPassword = async (
    data: NewPasswordFormData,
    { setSubmitting }: FormikHelpers<NewPasswordFormData>
  ): Promise<void> => {
    try {
      await Auth.resetUserPassword({ newPassword: data.password1, verificationToken });
      dispatch(
        enqueueSnackbar({
          message: 'userPasswordReset',
          options: { variant: 'success' },
        })
      );
      navigate('/');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'genericErrorOccurred';
      dispatch(
        enqueueSnackbar({
          message: errorMessage,
          options: { variant: 'error' },
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!verificationToken) {
    return <Navigate to='/' />;
  }

  return (
    <Container maxWidth='sm'>
      <Typography
        variant='h4'
        color='primary'
        display='inline'
      >
        {intl.formatMessage({ id: 'passwordResetTitle' })}
      </Typography>
      <Formik<NewPasswordFormData>
        initialValues={{ password1: '', password2: '' }}
        validationSchema={ValidationSchema}
        onSubmit={resetUserPassword}
      >
        {({ isSubmitting, isValid, dirty }) => (
          <Form>
            <FormikTextField
              name='password1'
              label={intl.formatMessage({ id: 'newPassword' })}
            />
            <FormikTextField
              name='password2'
              label={intl.formatMessage({ id: 'retypeNewPassword' })}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type='submit'
                label={intl.formatMessage({ id: 'submit' })}
                disabled={isSubmitting || !isValid || !dirty}
              />
            </div>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default NewPassword;
